// ============================================
// AlpenScreen - Super Admin Subscription Management Component
// File: frontend/src/components/SuperAdminSubscriptions.js
// Purpose: Manage all subscriptions, MRR, and widgets (MOBILE RESPONSIVE + i18n)
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  CreditCard,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SuperAdminSubscriptions = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState([]);
  const [summary, setSummary] = useState({
    total_mrr: 0,
    active_subscriptions: 0,
    trial_subscriptions: 0,
    total_organizations: 0
  });
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [editForm, setEditForm] = useState({
    tier: '',
    status: '',
    screen_limit: null
  });

  // Load subscriptions
  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load subscriptions');

      const data = await response.json();
      setSubscriptions(data.subscriptions);
      setSummary(data.summary);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      alert(t('superAdmin.subscriptions.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sub) => {
    setEditingOrg(sub.organization_id);
    setEditForm({
      tier: sub.tier || 'starter',
      status: sub.subscription_status || 'trial',
      screen_limit: sub.screen_limit
    });
  };

  const handleSave = async (orgId) => {
    try {
      const token = localStorage.getItem('token');

      // Update tier
      await fetch(`${API_URL}/superadmin/subscriptions/${orgId}/tier`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier: editForm.tier })
      });

      // Update status
      await fetch(`${API_URL}/superadmin/subscriptions/${orgId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: editForm.status })
      });

      // Update screen limit if changed
      if (editForm.screen_limit !== null) {
        await fetch(`${API_URL}/superadmin/subscriptions/${orgId}/screen-limit`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ screen_limit: editForm.screen_limit })
        });
      }

      alert(t('superAdmin.subscriptions.updateSuccess'));
      setEditingOrg(null);
      loadSubscriptions();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert(t('superAdmin.subscriptions.updateFailed'));
    }
  };

  const handleActivateWidget = async (orgId, featureId) => {
    if (!window.confirm(t('superAdmin.subscriptions.confirmActivate'))) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/superadmin/subscriptions/${orgId}/widgets/${featureId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to activate widget');
      }

      alert(t('superAdmin.subscriptions.widgetActivated'));
      loadSubscriptions();
    } catch (error) {
      console.error('Error activating widget:', error);
      alert(t('superAdmin.subscriptions.widgetActivationFailed'));
    }
  };

  const handleDeactivateWidget = async (orgId, featureId) => {
    if (!window.confirm(t('superAdmin.subscriptions.confirmDeactivate'))) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${API_URL}/superadmin/subscriptions/${orgId}/widgets/${featureId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(t('superAdmin.subscriptions.widgetDeactivated'));
      loadSubscriptions();
    } catch (error) {
      console.error('Error deactivating widget:', error);
      alert(t('superAdmin.subscriptions.updateFailed'));
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      {/* MRR Summary Cards - Mobile Responsive Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 md:p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-green-100 text-xs md:text-sm font-medium">
                {t('superAdmin.subscriptions.totalMRR')}
              </p>
              <p className="text-xl md:text-3xl font-bold mt-1 md:mt-2">CHF {summary.total_mrr}</p>
            </div>
            <DollarSign className="hidden md:block w-12 h-12 text-green-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 md:p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-blue-100 text-xs md:text-sm font-medium">
                {t('superAdmin.subscriptions.activeSubs')}
              </p>
              <p className="text-xl md:text-3xl font-bold mt-1 md:mt-2">{summary.active_subscriptions}</p>
            </div>
            <CreditCard className="hidden md:block w-12 h-12 text-blue-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-3 md:p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-yellow-100 text-xs md:text-sm font-medium">
                {t('superAdmin.subscriptions.trialSubs')}
              </p>
              <p className="text-xl md:text-3xl font-bold mt-1 md:mt-2">{summary.trial_subscriptions}</p>
            </div>
            <TrendingUp className="hidden md:block w-12 h-12 text-yellow-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 md:p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-purple-100 text-xs md:text-sm font-medium">
                {t('superAdmin.subscriptions.totalOrgs')}
              </p>
              <p className="text-xl md:text-3xl font-bold mt-1 md:mt-2">{summary.total_organizations}</p>
            </div>
            <Users className="hidden md:block w-12 h-12 text-purple-200 opacity-80" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-3 md:p-6 text-white shadow-lg">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
    <div>
      <p className="text-pink-100 text-xs md:text-sm font-medium">
        Widget MRR
      </p>
      <p className="text-xl md:text-3xl font-bold mt-1 md:mt-2">
        CHF {subscriptions.reduce((sum, sub) => sum + (sub.widget_mrr || 0), 0)}
      </p>
    </div>
    <Zap className="hidden md:block w-12 h-12 text-pink-200 opacity-80" />
  </div>
</div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {t('superAdmin.subscriptions.allSubscriptions')}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.organization')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.subscriptions.tier')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.subscriptions.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.screens')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  MRR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.subscriptions.widgets')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('superAdmin.subscriptions.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {subscriptions.map((sub) => (
                <tr key={sub.organization_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {sub.organization_name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {sub.user_count} {t('superAdmin.users').toLowerCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingOrg === sub.organization_id ? (
                      <select
                        value={editForm.tier}
                        onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                        className="text-sm border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="starter">{t('superAdmin.subscriptions.starter')}</option>
                        <option value="business">{t('superAdmin.subscriptions.business')}</option>
                        <option value="enterprise">{t('superAdmin.subscriptions.enterprise')}</option>
                      </select>
                    ) : (
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        sub.tier === 'enterprise' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        sub.tier === 'business' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {t(`superAdmin.subscriptions.${sub.tier}`) || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingOrg === sub.organization_id ? (
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="text-sm border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="trial">{t('superAdmin.subscriptions.trial')}</option>
                        <option value="active">{t('superAdmin.subscriptions.active')}</option>
                        <option value="cancelled">{t('superAdmin.subscriptions.cancelled')}</option>
                        <option value="past_due">{t('superAdmin.subscriptions.pastDue')}</option>
                        <option value="paused">{t('superAdmin.subscriptions.paused')}</option>
                      </select>
                    ) : (
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        sub.subscription_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        sub.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {t(`superAdmin.subscriptions.${sub.subscription_status}`) || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {editingOrg === sub.organization_id ? (
                      <input
                        type="number"
                        value={editForm.screen_limit || ''}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          screen_limit: e.target.value ? parseInt(e.target.value) : null 
                        })}
                        placeholder={t('superAdmin.subscriptions.unlimited')}
                        className="w-20 text-sm border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                    ) : (
                      `${sub.screen_count} / ${sub.screen_limit || '∞'}`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
    CHF {sub.total_mrr}
  </div>
  {sub.widget_mrr > 0 && (
    <div className="text-xs text-gray-500 dark:text-gray-400">
      Base: CHF {sub.base_mrr} + Widgets: CHF {sub.widget_mrr}
    </div>
  )}
</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {sub.active_widget_count} {t('superAdmin.subscriptions.active').toLowerCase()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingOrg === sub.organization_id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(sub.organization_id)}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          title={t('superAdmin.subscriptions.save')}
                        >
                          <Save size={18} />
                        </button>
                        <button
                          onClick={() => setEditingOrg(null)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title={t('superAdmin.subscriptions.cancel')}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(sub)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title={t('superAdmin.subscriptions.edit')}
                      >
                        <Edit size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 px-2">
          {t('superAdmin.subscriptions.allSubscriptions')}
        </h2>
        
        {subscriptions.map((sub) => (
          <div 
            key={sub.organization_id} 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
          >
            {/* Card Header */}
            <div 
              className="px-4 py-3 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer"
              onClick={() => setExpandedOrg(expandedOrg === sub.organization_id ? null : sub.organization_id)}
            >
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  {sub.organization_name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {sub.user_count} {t('superAdmin.users').toLowerCase()} • {sub.screen_count} screens
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
    CHF {sub.total_mrr}
  </span>
  {sub.widget_mrr > 0 && (
    <div className="text-xs text-gray-500 dark:text-gray-400">
      +CHF {sub.widget_mrr} widgets
    </div>
  )}
</div>
                {expandedOrg === sub.organization_id ? (
                  <ChevronUp size={20} className="text-gray-500" />
                ) : (
                  <ChevronDown size={20} className="text-gray-500" />
                )}
              </div>
            </div>

            {/* Expanded Content */}
            {expandedOrg === sub.organization_id && (
              <div className="px-4 py-3 space-y-3">
                {/* Tier */}
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                    {t('superAdmin.subscriptions.tier')}
                  </label>
                  {editingOrg === sub.organization_id ? (
                    <select
                      value={editForm.tier}
                      onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                      className="w-full text-sm border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    >
                      <option value="starter">{t('superAdmin.subscriptions.starter')}</option>
                      <option value="business">{t('superAdmin.subscriptions.business')}</option>
                      <option value="enterprise">{t('superAdmin.subscriptions.enterprise')}</option>
                    </select>
                  ) : (
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                      sub.tier === 'enterprise' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      sub.tier === 'business' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {t(`superAdmin.subscriptions.${sub.tier}`) || 'N/A'}
                    </span>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                    {t('superAdmin.subscriptions.status')}
                  </label>
                  {editingOrg === sub.organization_id ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full text-sm border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    >
                      <option value="trial">{t('superAdmin.subscriptions.trial')}</option>
                      <option value="active">{t('superAdmin.subscriptions.active')}</option>
                      <option value="cancelled">{t('superAdmin.subscriptions.cancelled')}</option>
                      <option value="past_due">{t('superAdmin.subscriptions.pastDue')}</option>
                      <option value="paused">{t('superAdmin.subscriptions.paused')}</option>
                    </select>
                  ) : (
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                      sub.subscription_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      sub.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {t(`superAdmin.subscriptions.${sub.subscription_status}`) || 'N/A'}
                    </span>
                  )}
                </div>

                {/* Screen Limit */}
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                    {t('superAdmin.subscriptions.screenLimit')}
                  </label>
                  {editingOrg === sub.organization_id ? (
                    <input
                      type="number"
                      value={editForm.screen_limit || ''}
                      onChange={(e) => setEditForm({ 
                        ...editForm, 
                        screen_limit: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      placeholder={t('superAdmin.subscriptions.unlimited')}
                      className="w-full text-sm border rounded px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    />
                  ) : (
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {sub.screen_count} / {sub.screen_limit || '∞'}
                    </span>
                  )}
                </div>

                {/* Widgets */}
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                    {t('superAdmin.subscriptions.activeWidgets')}
                  </label>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {sub.active_widget_count} {t('superAdmin.subscriptions.active').toLowerCase()}
                  </span>
                </div>

                {/* Actions */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  {editingOrg === sub.organization_id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(sub.organization_id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
                      >
                        <Save size={16} />
                        {t('superAdmin.subscriptions.save')}
                      </button>
                      <button
                        onClick={() => setEditingOrg(null)}
                        className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
                      >
                        <X size={16} />
                        {t('superAdmin.subscriptions.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(sub)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Edit size={16} />
                      {t('superAdmin.subscriptions.editSubscription')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2 md:gap-3">
          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">{t('superAdmin.subscriptions.infoTitle')}</p>
            <p>{t('superAdmin.subscriptions.infoText')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSubscriptions;